import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import {
  ResponseTextSubTypeEnum,
  ResponseTypeEnum,
  type TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { isTableSectionNode } from 'src/features/templateEdit/section/table/model/tableSection'
import {
  setTemplateReportNameRuleErrorMessage,
  updateTemplateReportNamingRules,
} from 'src/state/ducks/templates/actions'
import type { RootState } from 'src/state/store'
import { OptionToggleSwitch } from './OptionToggleSwitch'

const ReportNamingRuleRow = () => {
  const dispatch = useDispatch()

  const { templateNodes } = useSelector(
    (state: RootState) => state.templatesState.templatePages,
  )
  const {
    templateReportNamingRulesTemplateNodeUuid,
    isReportNamingRulesEnabled,
  } = useSelector((state: RootState) => state.templatesState.templatePages)

  // 例外となるノード（繰り返しセクション配下、および表セクション配下）をキャッシュ。
  const exceptionQuestionNodeUuid = useCallback(() => {
    const ids: number[] = []
    const traverseNodes = (nodes?: number[]) => {
      if (!nodes) return
      nodes.forEach(nodeId => {
        const targetNode = templateNodes[nodeId]
        if (!targetNode) return
        ids.push(targetNode.id)
        if (
          // nodesがある場合は再帰的に処理
          targetNode.nodes.length > 0
        ) {
          traverseNodes(targetNode.nodes)
        }
      })
    }

    Object.values(templateNodes)
      .filter(
        node =>
          node.type === TemplateNodeTypeEnum.Section &&
          (node.section?.isRepeat || isTableSectionNode(node)),
      )
      .forEach(section => traverseNodes(section.nodes))

    return ids
  }, [templateNodes])

  // 選択可能なテキスト質問ノードをキャッシュ
  const textQuestionNodes = useCallback(
    () =>
      Object.values(templateNodes).filter(
        node =>
          node.type === TemplateNodeTypeEnum.Question &&
          node.question?.responseType === ResponseTypeEnum.TEXT &&
          node.id &&
          !exceptionQuestionNodeUuid().includes(node.id) &&
          node.question?.name &&
          node.question?.responseTexts?.[0]?.subType ===
            ResponseTextSubTypeEnum.SINGLE_LINE, // 複数行の場合は除外
      ),
    [templateNodes, exceptionQuestionNodeUuid],
  )

  const [filteredTextQuestionNodes, setFilteredTextQuestionNodes] =
    useState(textQuestionNodes())

  const filteredTextQuestionNodeUUIDSet = useMemo(
    () => new Set(filteredTextQuestionNodes.map(node => node.uuid)),
    [filteredTextQuestionNodes],
  )

  const updateFilterdNodes = useCallback(() => {
    setFilteredTextQuestionNodes(textQuestionNodes())
  }, [textQuestionNodes])

  const templateReportNameRuleErrorMessage = useSelector(
    (state: RootState) =>
      state.templatesState.templatePages.templateReportNameRuleErrorMessage,
    shallowEqual,
  )

  const updateReportNamingRule = useCallback(
    ({ uuid, isEnabled }: { uuid: string; isEnabled: boolean }) => {
      dispatch(updateTemplateReportNamingRules(uuid, isEnabled))
      dispatch(setTemplateReportNameRuleErrorMessage(''))
    },
    [dispatch],
  )

  // 編集開始時に、既にレポート名設定に設定済みの質問名を表示するために表示可能な質問を取得
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  useEffect(() => {
    if (
      isInitialLoadComplete ||
      templateReportNamingRulesTemplateNodeUuid === ''
    ) {
      return
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    updateFilterdNodes()
    setIsInitialLoadComplete(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateReportNamingRulesTemplateNodeUuid])

  const selectedNode = filteredTextQuestionNodeUUIDSet.has(
    templateReportNamingRulesTemplateNodeUuid,
  )
    ? (filteredTextQuestionNodes.find(
        node => node.uuid === templateReportNamingRulesTemplateNodeUuid,
      ) ?? null)
    : null

  return (
    <Stack spacing={1} sx={{ mx: 2.5 }}>
      <OptionToggleSwitch
        isChecked={isReportNamingRulesEnabled}
        onChange={checked =>
          updateReportNamingRule({ uuid: '', isEnabled: checked })
        }
        label="作成されるレポート名に質問の回答を加える"
      />
      <Typography variant="caption" color="text.secondary">
        例：{'回答結果_ひな形名 もしくは 回答結果_スケジュール名'}
      </Typography>
      {isReportNamingRulesEnabled && (
        <Stack spacing={1}>
          <Autocomplete<TemplateNodeSchema, false, true>
            disableClearable
            options={filteredTextQuestionNodes}
            getOptionLabel={option => option.question?.name ?? ''}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
            value={selectedNode ?? undefined}
            onChange={(_, value) => {
              updateReportNamingRule({
                uuid: value.uuid ?? '',
                isEnabled: true,
              })
            }}
            onOpen={updateFilterdNodes}
            renderInput={params => (
              <TextField {...params} size="small" placeholder="質問を選択" />
            )}
          />
          <Typography variant="caption" color="text.secondary">
            1行フォーマットのテキスト質問のみ選択できます。
            <br />
            繰り返しセクションにある質問は選択できません。
          </Typography>
          {templateReportNameRuleErrorMessage && (
            <Typography id="error-message" color="error" fontWeight="bold">
              {templateReportNameRuleErrorMessage}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default ReportNamingRuleRow
